import ScButton from "@/src/components/ui-kit/button";
import { Step1FormValues, Step2FormValues } from "@/src/models/event.model";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import { SelectableItem } from "../../action-modals/item-selection/item-row";
import { ReviewRow } from "./review-row";
import { useSelectedGroupItems } from "@/src/services/api/groups/groups.hooks";

const extractTimeFromISO = (iso: string) => {
  if (!iso) return "";
  const date = new Date(iso);
  return isNaN(date.getTime()) ? iso : format(date, "h:mm a");
};

const POLICY_LABELS: Record<Step2FormValues["registrationPolicy"], string> = {
  None: "LABEL_NONE",
  Required: "LABEL_REQUIRED",
  Invite: "LABEL_INVITE_ONLY",
};

const VISIBILITY_LABELS: Record<Step2FormValues["visibility"], string> = {
  Public: "LABEL_PUBLIC",
  Group: "LABEL_GROUP",
};

type Step3ReviewProps = Readonly<{
  page1Data: Step1FormValues | null;
  page2Data: Step2FormValues | null;
  categories: SelectableItem[];
  onBack: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isEditMode?: boolean;
}>;

const Step3Review = ({
  page1Data,
  page2Data,
  categories,
  onBack,
  onSubmit,
  onCancel,
  isSubmitting,
  isEditMode,
}: Step3ReviewProps) => {
  const { t } = useTranslation();

  const selectedCategories = useMemo(
    () =>
      (page1Data?.categoryIds ?? [])
        .map((id) => categories.find((cat) => cat.itemId === id))
        .filter(Boolean) as SelectableItem[],
    [page1Data?.categoryIds, categories],
  );

  const selectedGroups = useSelectedGroupItems(page2Data?.groupIds);

  const PAGE1_ROWS = useMemo(
    () => [
      { label: t("LABEL_EVENT_TITLE"), value: page1Data?.title },
      { label: t("LABEL_DESCRIPTION"), value: page1Data?.description },
      {
        label: t("LABEL_SINGLE_DAY_EVENT"),
        value: page1Data?.singleDay ? t("LABEL_YES") : t("LABEL_NO"),
      },
      { label: t("LABEL_START_DATE"), value: page1Data?.startDate },
      { label: t("LABEL_END_DATE"), value: page1Data?.endDate },
      {
        label: t("LABEL_START_TIME"),
        value: extractTimeFromISO(page1Data?.startTime ?? ""),
      },
      {
        label: t("LABEL_END_TIME"),
        value: extractTimeFromISO(page1Data?.endTime ?? ""),
      },
      { label: t("LABEL_LOCATION"), value: page1Data?.locationName },
    ],
    [t, page1Data],
  );

  const renderChips = (items: SelectableItem[]) => (
    <View className="flex-row flex-wrap gap-1.5 mt-0.5 ">
      {items.map((item) => (
        <View
          key={item.itemId}
          className="bg-chalk border border-ash-1 rounded-full px-3 py-0.5"
        >
          <Text className="text-xs font-medium text-primary">
            {item.displayName}
          </Text>
        </View>
      ))}
    </View>
  );

  return (
    <View>
      <Text className="font-medium text-onyx">{t("LABEL_EVENT_DETAILS")}</Text>

      {PAGE1_ROWS.map((row) => (
        <ReviewRow key={row.label} label={row.label} value={row.value} />
      ))}

      {selectedCategories.length > 0 && (
        <ReviewRow
          label={t("LABEL_CATEGORY")}
          value={renderChips(selectedCategories)}
        />
      )}

      <Text className="font-medium mt-4 text-onyx">
        {t("LABEL_EVENT_SETTINGS")}
      </Text>

      <ReviewRow
        label={t("LABEL_VISIBILITY_OPTIONS")}
        value={t(VISIBILITY_LABELS[page2Data?.visibility ?? "Public"])}
      />

      {page2Data?.visibility === "Group" && selectedGroups.length > 0 && (
        <ReviewRow
          label={t("LABEL_GROUPS")}
          value={renderChips(selectedGroups)}
        />
      )}

      <ReviewRow
        label={t("LABEL_REGISTRATION_POLICY")}
        value={t(POLICY_LABELS[page2Data?.registrationPolicy ?? "None"])}
      />
      {page2Data?.registrationPolicy === "Required" && (
        <ReviewRow
          label={t("LABEL_EVENT_CAPACITY")}
          value={`${page2Data.capacity} ${t("LABEL_PARTICIPANTS")}`}
        />
      )}

      <View className="flex-row justify-between mt-6">
        <ScButton text={t("BTN_CANCEL")} mode="outlined" onPress={onCancel} />
        <View className="flex-row gap-4">
          <ScButton text={t("BTN_PREVIOUS")} mode="outlined" onPress={onBack} />
          <ScButton
            text={t(isEditMode ? "BTN_UPDATE" : "BTN_CREATE")}
            onPress={onSubmit}
            loading={isSubmitting}
          />
        </View>
      </View>
    </View>
  );
};

export default Step3Review;
